import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import Button from "../Button";
import Modal from "../Modal";
import { toast } from "react-toastify";
import { 
  deleteReplay, 
  formatDuration, 
  formatFileSize, 
  formatRelativeTime,
  getReplayThumbnailUrl 
} from "../../services/replaysApi";

// Icons
import { ReactComponent as PlayIcon } from "../../img/icons/play.svg";
import { ReactComponent as DeleteIcon } from "../../img/icons/delete.svg";
import { ReactComponent as EyeIcon } from "../../img/icons/eye-on.svg";

const ReplayCard = ({ replay, onDelete }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const theme = useTheme();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteReplay(replay.id, false); // Soft delete
      toast.success("Replay deleted successfully");
      setShowDeleteModal(false);
      if (onDelete) {
        onDelete(replay.id);
      }
    } catch (error) {
      toast.error("Failed to delete replay");
      console.error("Delete error:", error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CardContainer>
        <ThumbnailContainer>
          <Link to={`/replay/${replay.id}`}>
            <Thumbnail 
              src={getReplayThumbnailUrl(replay.thumbnailPath)} 
              alt={replay.title}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/640x360/1a1a2e/ffffff?text=No+Thumbnail';
              }}
            />
            <PlayOverlay>
              <PlayButton>
                <PlayIcon />
              </PlayButton>
            </PlayOverlay>
            {replay.duration && (
              <Duration>{formatDuration(replay.duration)}</Duration>
            )}
          </Link>
        </ThumbnailContainer>

        <CardBody>
          <CardHeader>
            <Link to={`/replay/${replay.id}`}>
              <Title>{replay.title}</Title>
            </Link>
            {onDelete && (
              <DeleteButton 
                icon 
                variant="danger" 
                size="small"
                onClick={() => setShowDeleteModal(true)}
                title="Delete replay"
              >
                <DeleteIcon />
              </DeleteButton>
            )}
          </CardHeader>

          {replay.stream_games && (
            <GameName>
              <Link to={`/game/${replay.stream_games.id}`}>
                {replay.stream_games.name}
              </Link>
            </GameName>
          )}

          <MetaInfo>
            <MetaItem>
              <EyeIcon style={{ width: "14px", height: "14px", marginRight: "4px" }} />
              {replay.viewCount || 0} views
            </MetaItem>
            <MetaItem>
              {replay.fileSize && formatFileSize(parseInt(replay.fileSize))}
            </MetaItem>
            <MetaItem>
              {formatRelativeTime(replay.createdAt)}
            </MetaItem>
          </MetaInfo>
        </CardBody>
      </CardContainer>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={showDeleteModal} 
        onRequestClose={() => setShowDeleteModal(false)}
        hideModal={() => setShowDeleteModal(false)}
      >
        <DeleteModal>
          <h2>Delete Replay?</h2>
          <p>Are you sure you want to delete "{replay.title}"?</p>
          <p style={{ fontSize: "14px", color: theme.color.darkerText }}>
            This action can be undone from the admin panel.
          </p>
          <ButtonGroup>
            <Button 
              variant="secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </ButtonGroup>
        </DeleteModal>
      </Modal>
    </>
  );
};

// Styled Components
const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 320px;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 576px) {
    max-width: 100%;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.background};

  a {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.3s ease;
  opacity: 0;

  ${ThumbnailContainer}:hover & {
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    fill: white;
    margin-left: 4px;
  }

  &:hover {
    transform: scale(1.1);
  }
`;

const Duration = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const CardBody = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text};
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: ${({ theme }) => theme.color.primary};
  }
`;

const DeleteButton = styled(Button)`
  min-width: 32px;
  width: 32px;
  height: 32px;
  padding: 6px;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const GameName = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.color.darkerText};
  
  a {
    color: ${({ theme }) => theme.color.darkerText};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme.color.primary};
    }
  }
`;

const MetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.darkerText};
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
`;

const DeleteModal = styled.div`
  padding: 20px;
  max-width: 400px;

  h2 {
    margin: 0 0 16px 0;
    font-size: 24px;
    color: ${({ theme }) => theme.color.text};
  }

  p {
    margin: 0 0 12px 0;
    color: ${({ theme }) => theme.color.text};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;

  button {
    min-width: 100px;
  }
`;

export default ReplayCard;

